// =============================================================================
// error-translate.ts — UX-2: turn raw failure strings into an { code, message }
// an operator can act on. Used by the workers when marking a job/cell failed, and
// by the frontend as a fallback map. One place, so backend + frontend agree.
// =============================================================================

export interface TranslatedError { code: string; message: string; retryable: boolean }

/** Map a raw error string to an operator-facing code + message. */
export function translateError(raw: string): TranslatedError {
  const s = (raw || "").toLowerCase();

  // ── download / source problems (the operator's URL) ──────────────────────
  if (s.includes("private") || s.includes("unavailable") || s.includes("removed") || s.includes("404"))
    return { code: "SOURCE_UNAVAILABLE", message: "Couldn't fetch the video — it may be private, removed, or region-locked. Check the URL opens.", retryable: false };
  if (s.includes("yt-dlp") || s.includes("download") || s.includes("unsupported url"))
    return { code: "DOWNLOAD_FAILED", message: "Download failed — the link may be unsupported or temporarily blocked. Try again or use a different URL.", retryable: true };

  // ── media / content ──────────────────────────────────────────────────────
  if (s.includes("no audio") || s.includes("transcript") || s.includes("whisper"))
    return { code: "NO_SPEECH", message: "No speech found (music-only video). The clone still works without a script.", retryable: false };
  if (s.includes("ffmpeg") || s.includes("ffprobe") || s.includes("duration"))
    return { code: "MEDIA_UNREADABLE", message: "The video file couldn't be read. It may be corrupt or an unusual format.", retryable: true };

  // ── model / provider ──────────────────────────────────────────────────────
  if (s.includes("moderated") || s.includes("moderation"))
    return { code: "MODERATED", message: "The image model flagged this prompt. Re-roll — it usually clears on a retry.", retryable: true };
  if (s.includes("bfl") || s.includes("flux"))
    return { code: "IMAGE_MODEL_ERROR", message: "The image model had an error. Re-roll; if it persists, escalate.", retryable: true };
  if (s.includes("kling") || s.includes("generate_clip"))
    return { code: "RENDER_ERROR", message: "The render model had an error. Re-render; if it persists, escalate.", retryable: true };
  if (s.includes("openai") || s.includes("gpt-4o") || s.includes("vision"))
    return { code: "VISION_ERROR", message: "The analysis model had an error. Retry; if it persists, escalate.", retryable: true };

  // ── infra / auth ──────────────────────────────────────────────────────────
  if (s.includes("401") || s.includes("403") || s.includes("unauthorized") || s.includes("token"))
    return { code: "AUTH_ERROR", message: "A service credential failed — this is a system issue, not your input. Escalate.", retryable: false };
  if (s.includes("timeout") || s.includes("timed out") || s.includes("etimedout"))
    return { code: "TIMEOUT", message: "A step timed out (slow provider or large video). Retry.", retryable: true };
  if (s.includes("storage") || s.includes("upload"))
    return { code: "STORAGE_ERROR", message: "Couldn't save the result to storage — a system issue. Escalate.", retryable: false };

  // ── unknown ───────────────────────────────────────────────────────────────
  return { code: "SYSTEM_ERROR", message: "Something went wrong. Retry once; if it repeats, escalate.", retryable: true };
}
